<script setup lang="ts">
import AppTopbar from './AppTopbar.vue';
import {
    computed,
    watch,
    ref,
    onBeforeUnmount,
    type ComponentPublicInstance,
} from 'vue';
import { useAppLayoutStateStore } from '../stores/app-layout-state';
import AppConfigPanel from './AppConfigPanel.vue';
import { useColorScheme } from '@precon-tools-suite-by-shared/vue-color-schemes';
import { useAppConfigStore } from '../stores/app-config-store';
import AppBreadcrumb from './AppBreadcrumb.vue';
import AppRightPanel from './AppRightPanel.vue';
import ColorFooter from './ColorFooter.vue';

const outsideClickListener = ref<null | ((event: MouseEvent) => void)>(null);
const topbarRef = ref<ComponentPublicInstance<typeof AppTopbar> | null>(null);

// COLOR SCHEME
const stateStore = useAppLayoutStateStore();
const configStore = useAppConfigStore();
const { scheme } = useColorScheme();

function isOutsideClicked(event: MouseEvent) {
    if (!topbarRef.value) return;

    const sidebarEl = topbarRef?.value.$el.querySelector('.layout-sidebar');
    const topbarEl = topbarRef?.value.$el.querySelector(
        '.topbar-start > button'
    );

    return !(
        sidebarEl.isSameNode(event.target) ||
        sidebarEl.contains(event.target) ||
        topbarEl.isSameNode(event.target) ||
        topbarEl.contains(event.target)
    );
}

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                stateStore.overlayMenuActive = false;
                stateStore.overlaySubmenuActive = false;
                stateStore.staticMenuMobileActive = false;
                stateStore.menuHoverActive = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
};

watch(
    () => stateStore.isSidebarActive,
    (newVal) => {
        if (newVal) {
            bindOutsideClickListener();
        } else {
            unbindOutsideClickListener();
        }
    }
);

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

// STYLING
const containerClass = computed(() => [
    'flex',
    'flex-column',
    'layout-container',
    'layout-wrapper',
    'layout-' + scheme.value,
    'layout-' + configStore.menuTheme + '-menu',
    'layout-' + configStore.topbarTheme + '-topbar',
    'layout-' + configStore.menuMode,
    {
        'layout-static-inactive':
            stateStore.staticMenuDesktopInactive && configStore.staticMode,
        'layout-overlay-active': stateStore.overlayMenuActive,
        'layout-mobile-active': stateStore.staticMenuMobileActive,
        'layout-sidebar-active': stateStore.sidebarActive,
        'layout-sidebar-anchored': configStore.sidebarAnchored,
        'p-input-filled': configStore.inputStyle === 'filled',
        'p-ripple-disabled': !configStore.ripple,
    },
]);

const routeView = ref();
const routeSubstitutions = computed(() => routeView.value?.routeSubstitutions);
</script>

<template>
    <div v-bind="$attrs" :class="containerClass" :data-theme="scheme">
        <AppTopbar />
        <AppConfigPanel />

        <div
            id="layout-content-wrapper"
            class="flex flex-column flex-grow-1 layout-content-wrapper"
        >
            <div class="layout-content">
                <AppBreadcrumb :substitutions="routeSubstitutions" />
                <router-view v-slot="{ Component }">
                    <component :is="Component" ref="routeView" />
                </router-view>
            </div>

            <div v-if="$slots.footer" class="layout-footer">
                <slot name="footer" />
            </div>
        </div>
        <ColorFooter />

        <AppRightPanel />

        <div class="layout-mask modal-in" />
    </div>
</template>
